import { Palette } from "@mui/material";
import { hexToRGBA } from "@utils";
import { getDate, getMonth, getYear, lastDayOfMonth } from "date-fns";
import { isNil } from "lodash";

export const getMonthRangeDate = ({
  year,
  month,
  pickEndOfMonth = false,
}: {
  year: number;
  month: number;
  pickEndOfMonth?: boolean;
}) => {
  const date = new Date(year, month, 1, 23, 59, 59);
  if (!pickEndOfMonth) return date;

  const lastDay = lastDayOfMonth(date);
  return new Date(
    getYear(lastDay),
    getMonth(lastDay),
    getDate(lastDay),
    23,
    59,
    59,
  );
};

export const setStartMonthRangeStyles = ({
  element,
  month,
  palette,
}: {
  element?: Element | null;
  month: number | null;
  palette: Palette;
}) => {
  if (!element || isNil(month)) return;

  const buttons = getButtonList({ element });
  let foundIndex = false;
  buttons?.forEach((btn, btnIdx) => {
    btn.disabled = false;

    if (btnIdx === month) {
      foundIndex = true;
      applyButtonStyles(btn, {
        backgroundColor: palette.primary.main,
        color: palette.common.white,
        border: `1px solid ${palette.primary.dark}`,
      });
    } else if (!foundIndex) {
      applyButtonStyles(btn, {
        backgroundColor: palette.common.white,
        color: palette.common.black,
        border: `1px solid ${hexToRGBA(palette.common.black, 0.1)}`,
      });
    } else {
      applyButtonStyles(btn, {
        backgroundColor: hexToRGBA(palette.primary.main, 0.1),
        color: palette.common.black,
        border: "none",
      });
    }
  });
};

export const setEndMonthRangeStyles = ({
  element,
  month,
  palette,
}: {
  element?: Element | null;
  month: number | null;
  palette: Palette;
}) => {
  if (!element || isNil(month)) return;

  const buttons = getButtonList({ element });
  let foundIndex = false;
  buttons?.forEach((btn, btnIdx) => {
    btn.disabled = false;

    if (btnIdx === month) {
      foundIndex = true;
      applyButtonStyles(btn, {
        backgroundColor: palette.primary.main,
        color: palette.common.white,
        border: `1px solid ${palette.primary.dark}`,
      });
    } else if (!foundIndex) {
      applyButtonStyles(btn, {
        backgroundColor: hexToRGBA(palette.primary.main, 0.1),
        color: palette.common.black,
        border: "none",
      });
    } else {
      applyButtonStyles(btn, {
        backgroundColor: palette.common.white,
        color: palette.common.black,
        border: `1px solid ${hexToRGBA(palette.common.black, 0.1)}`,
      });
    }
  });
};

export const clearStyles = ({
  element,
  palette,
}: {
  element?: Element | null;
  palette: Palette;
}) => {
  if (!element) return;

  const buttons = getButtonList({ element });
  buttons?.forEach((btn) => {
    btn.disabled = false;
    btn.style.backgroundColor = palette.common.white;
    btn.style.color = palette.common.black;
    btn.style.border = `1px solid ${hexToRGBA(palette.common.black, 0.1)}`;
  });
};

export const setButtonDisabledStyles = ({
  palette,
  startElement,
  endElement,
  endMonth,
  startMonth,
}: {
  palette: Palette;
  startElement: Element | null;
  endElement: Element | null;
  endMonth: Date | null;
  startMonth: Date | null;
}) => {
  if (!startElement || !endElement || !endMonth || !startMonth) return;

  const endMonthValue = getMonth(endMonth);
  const startMonthValue = getMonth(startMonth);

  processButtonList({
    element: startElement,
    isDisabledCondition: (btnIdx) => btnIdx >= endMonthValue,
    isHighlightedCondition: (btnIdx) => btnIdx === endMonthValue,
    isSelectedCondition: (btnIdx) => btnIdx === startMonthValue,
    palette,
  });

  processButtonList({
    element: endElement,
    isDisabledCondition: (btnIdx) => btnIdx <= startMonthValue,
    isHighlightedCondition: (btnIdx) => btnIdx === startMonthValue,
    isSelectedCondition: (btnIdx) => btnIdx === endMonthValue,
    palette,
  });
};

export const getButtonList = ({
  element,
  reverse = false,
}: {
  element: Element | null | undefined;
  reverse?: boolean;
}): HTMLButtonElement[] => {
  if (!element) return [];
  const buttons = Array.from(
    element.querySelectorAll("button"),
  ) as HTMLButtonElement[];
  return reverse ? buttons.reverse() : buttons;
};

const applyButtonStyles = (
  button: HTMLButtonElement,
  styles: {
    backgroundColor: string;
    color: string;
    border: string;
    fontWeight?: string;
  },
) => {
  button.style.backgroundColor = styles.backgroundColor;
  button.style.color = styles.color;
  button.style.border = styles.border;
  button.style.fontWeight = !!styles.fontWeight ? styles.fontWeight : "normal";
};

const processButtonList = ({
  element,
  isDisabledCondition,
  isHighlightedCondition,
  isSelectedCondition,
  palette,
}: {
  element: Element;
  isDisabledCondition: (index: number) => boolean;
  isHighlightedCondition: (index: number) => boolean;
  isSelectedCondition: (index: number) => boolean;
  palette: Palette;
}) => {
  const buttons = getButtonList({ element });

  buttons?.forEach((btn, btnIdx) => {
    const tabIndex = btn.attributes.getNamedItem("tabIndex")?.value;
    if (!tabIndex) return;

    if (isSelectedCondition(btnIdx)) {
      applyButtonStyles(btn, {
        backgroundColor: palette.primary.main,
        color: palette.common.white,
        border: `1px solid ${palette.primary.dark}`,
      });

      return;
    }

    if (isDisabledCondition(btnIdx)) {
      btn.disabled = true;
      applyButtonStyles(btn, {
        backgroundColor: palette.common.white,
        color: isHighlightedCondition(btnIdx)
          ? palette.primary.light
          : palette.text.disabled,
        fontWeight: isHighlightedCondition(btnIdx)
          ? "bold"
          : undefined,
        border: "none",
      });
    }
  });
};
